import { useEffect, useRef } from 'react';

import {
  isRouteErrorResponse,
  useLocation,
  useNavigate,
  useRouteError,
} from 'react-router-dom';

import { Box, Button, Stack, useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';

import { AppIcon } from '~components/shared/app-icon/app-icon';
import { Logo } from '~components/ui/logo/logo';
import { AppTypography } from '~components/ui/typography/app-typography';

import { FRONTEND_CONFIG } from '~app/config';
import { NotFoundPage } from '~app/pages/lessor/not-found/not-found-page';
import { ROUTES } from '~app/router/routes';

import { ReactComponent as IconRefresh } from '~assets/icons/icon-arrows-reload-1.svg';
import { ReactComponent as IconDashboard } from '~assets/icons/icon-dashboard.svg';
import { ReactComponent as IconWarning } from '~assets/icons/icon-octagon-warning.svg';

const CHUNK_RELOAD_ATTEMPTED_KEY = 'rentify_chunk_reload_attempted';
const CHUNK_RELOAD_ATTEMPT_TTL_MS = 5 * 60 * 1000;

type ChunkReloadAttempt = {
  pathname: string;
  search: string;
  createdAt: number;
};

const isChunkLoadError = (error: unknown) => {
  const errorName = error instanceof Error ? error.name : '';

  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  const normalizedError = `${errorName}\n${errorMessage}`.toLowerCase();

  return [
    'chunkloaderror',
    'loading chunk',
    'loading css chunk',
    'failed to fetch dynamically imported module',
    'importing a module script failed',
    'failed to load module script',
    'expected a javascript module script',
    'dynamically imported module',
  ].some((pattern) => normalizedError.includes(pattern));
};

const getSessionStorageItem = (key: string) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const setSessionStorageItem = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
};

const getChunkReloadAttempt = (): ChunkReloadAttempt | null => {
  const rawValue = getSessionStorageItem(CHUNK_RELOAD_ATTEMPTED_KEY);

  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue) as Partial<ChunkReloadAttempt>;

    if (
      typeof parsed.pathname !== 'string' ||
      typeof parsed.search !== 'string' ||
      typeof parsed.createdAt !== 'number'
    ) {
      return null;
    }

    return {
      pathname: parsed.pathname,
      search: parsed.search,
      createdAt: parsed.createdAt,
    };
  } catch {
    return null;
  }
};

const isFreshChunkReloadAttempt = (
  attempt: ChunkReloadAttempt | null,
  pathname: string,
  search: string,
) => {
  if (!attempt) return false;

  return (
    attempt.pathname === pathname &&
    attempt.search === search &&
    Date.now() - attempt.createdAt < CHUNK_RELOAD_ATTEMPT_TTL_MS
  );
};

const setChunkReloadAttempt = (attempt: ChunkReloadAttempt) => {
  setSessionStorageItem(CHUNK_RELOAD_ATTEMPTED_KEY, JSON.stringify(attempt));
};

const getRouteErrorException = (error: unknown) => {
  if (error instanceof Error) {
    return error;
  }

  if (isRouteErrorResponse(error)) {
    return new Error(
      `Route error ${error.status}: ${error.statusText || 'Unknown error'}`,
    );
  }

  return new Error('Unknown route error');
};

const stringifyRouteErrorData = (data: unknown) => {
  if (!data) return null;
  if (typeof data === 'string') return data;

  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
};

const getRouteErrorDetails = (error: unknown) => {
  if (error instanceof Error) {
    return [error.name, error.message, error.stack]
      .filter(Boolean)
      .join('\n\n');
  }

  if (isRouteErrorResponse(error)) {
    return [
      `Status: ${error.status}`,
      `Status text: ${error.statusText || 'Unknown error'}`,
      stringifyRouteErrorData(error.data),
    ]
      .filter(Boolean)
      .join('\n\n');
  }

  return stringifyRouteErrorData(error) || 'Unknown route error';
};

export const RouteErrorPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const error = useRouteError();
  const hasReportedError = useRef(false);

  const errorDetails = getRouteErrorDetails(error);

  const isNotFoundError = isRouteErrorResponse(error) && error.status === 404;
  const isChunkError = isChunkLoadError(error);
  const chunkReloadAttempt = getChunkReloadAttempt();

  const hasTriedChunkReload = isFreshChunkReloadAttempt(
    chunkReloadAttempt,
    location.pathname,
    location.search,
  );

  const shouldShowNewVersionMessage = isChunkError && !hasTriedChunkReload;

  const pageTitle = shouldShowNewVersionMessage
    ? 'Вийшла нова версія Rentify'
    : 'Не вдалося завантажити сторінку';

  const pageDescription = shouldShowNewVersionMessage
    ? 'Оновіть сторінку, щоб завантажити актуальну версію застосунку. Після оновлення ви зможете продовжити роботу.'
    : isChunkError
      ? 'Спробуйте оновити сторінку ще раз. Якщо помилка повторюється, ми вже отримали звіт і перевіримо проблему.'
      : 'Ми вже отримали звіт про помилку. Спробуйте оновити сторінку або поверніться на головну.';

  const refreshButtonText = shouldShowNewVersionMessage
    ? 'Оновити сторінку'
    : 'Оновити';

  const shouldShowDashboardButton = !shouldShowNewVersionMessage;

  const shouldShowErrorDetails =
    FRONTEND_CONFIG.VITE_NODE_ENV !== 'production' &&
    (import.meta.env.DEV || FRONTEND_CONFIG.VITE_SHOW_ERROR_DETAILS);

  const handleReload = () => {
    if (isChunkError) {
      setChunkReloadAttempt({
        pathname: location.pathname,
        search: location.search,
        createdAt: Date.now(),
      });
    }

    window.location.reload();
  };

  useEffect(() => {
    if (hasReportedError.current) return;
    if (isNotFoundError) return;

    hasReportedError.current = true;

    Sentry.withScope((scope) => {
      scope.setTag('source', 'react-router');
      scope.setTag('route_error', 'true');
      scope.setTag('chunk_load_error', String(isChunkError));
      scope.setTag('chunk_reload_attempted', String(hasTriedChunkReload));

      scope.setContext('route', {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      });

      if (isRouteErrorResponse(error)) {
        scope.setTag('status', String(error.status));
        scope.setContext('route_error_response', {
          status: error.status,
          statusText: error.statusText,
          data: error.data,
        });
      }

      if (isChunkError) {
        scope.setLevel('warning');
        scope.setFingerprint(['chunk-load-error']);
        scope.setContext('chunk_reload_attempt', {
          pathname: chunkReloadAttempt?.pathname,
          search: chunkReloadAttempt?.search,
          createdAt: chunkReloadAttempt?.createdAt,
          isFresh: hasTriedChunkReload,
          ttlMs: CHUNK_RELOAD_ATTEMPT_TTL_MS,
        });

        Sentry.captureException(getRouteErrorException(error));

        return;
      }

      Sentry.captureException(getRouteErrorException(error));
    });
  }, [
    error,
    chunkReloadAttempt,
    hasTriedChunkReload,
    isChunkError,
    isNotFoundError,
    location.hash,
    location.pathname,
    location.search,
  ]);

  if (isNotFoundError) {
    return <NotFoundPage />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: 3,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'neutral.100',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 520,
          height: 520,
          borderRadius: '50%',
          top: '46%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background:
            'radial-gradient(circle, rgba(13,110,253,0.10), rgba(52,201,123,0.08), transparent 68%)',
          filter: 'blur(56px)',
          zIndex: 0,
        }}
      />

      <Stack
        alignItems={'center'}
        textAlign={'center'}
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 560,
        }}
      >
        <Stack direction={'row'} gap={1.5} sx={{ mb: 5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              background: `linear-gradient(45deg, ${theme.palette.primaryTokens['25']}, ${theme.palette.green['25']})`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'neutral.100',
            }}
          >
            <AppIcon component={IconWarning} gradient gradientType={'3'} />
          </Box>

          <Stack mt={0.75}>
            <Logo width={130} height={40} />
          </Stack>
        </Stack>

        <AppTypography
          color={'fog.35'}
          sx={{
            mb: 2,
          }}
          size={'h4'}
          weight={'bold'}
        >
          {pageTitle}
        </AppTypography>

        <AppTypography size={'body-md'} color={'fog.45'} mb={4}>
          {pageDescription}
        </AppTypography>

        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
          <Button
            startIcon={<AppIcon component={IconRefresh} size={20} />}
            onClick={handleReload}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {refreshButtonText}
          </Button>

          {shouldShowDashboardButton && (
            <Button
              variant={'outlined'}
              endIcon={<AppIcon component={IconDashboard} size={20} />}
              onClick={() => navigate(ROUTES.DASHBOARD)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              На головну
            </Button>
          )}
        </Stack>

        {shouldShowErrorDetails && (
          <Box
            sx={{
              width: '100%',
              mt: 4,
              p: 2,
              border: '1px solid',
              borderColor: 'fog.90',
              borderRadius: '8px',
              backgroundColor: 'fog.98',
              textAlign: 'left',
            }}
          >
            <AppTypography
              size={'body-sm'}
              color={'fog.35'}
              weight={'semibold'}
            >
              Технічні деталі помилки
            </AppTypography>

            <Box
              component={'pre'}
              sx={{
                mt: 1,
                mb: 0,
                maxHeight: 220,
                overflow: 'auto',
                color: 'fog.45',
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              {errorDetails}
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
